/**
 * Four small noises, synthesised rather than downloaded.
 *
 * A knock is a sound. The product is named after one and has never made one,
 * and the cheapest honest version of it is Web Audio: a couple of oscillators
 * and a noise burst, a few hundred bytes of arithmetic, no files, no
 * dependency, nothing to fetch and nothing to license.
 *
 * EVERY ENTRY POINT FAILS SILENT. A browser with no `AudioContext`, a context
 * the autoplay policy will not resume, a page that has never been clicked — all
 * of them return without a sound and without an error. Audio is the last thing
 * in this product that should be allowed to throw.
 *
 * REDUCED MOTION SILENCES IT. That is the prototype's rule and it is the right
 * one here for a reason the CSS guard cannot express: `prefers-reduced-motion`
 * is the only signal a browser gives us that somebody wants the interface to
 * stop performing, and an unannounced noise is the loudest performance there
 * is. It is asked on every call rather than cached, because the preference can
 * change under a live page.
 *
 * The context is created lazily and kept: one per document, opened by the first
 * sound rather than at import, so a session that never makes a noise never
 * opens an audio device.
 */

let ctx: AudioContext | null = null;

function silenced(): boolean {
  try {
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  } catch {
    return false;
  }
}

function audio(): AudioContext | null {
  if (typeof window === "undefined" || silenced()) return null;
  const Ctor =
    window.AudioContext ??
    (window as Window & { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (!Ctor) return null;
  try {
    ctx ??= new Ctor();
    if (ctx.state === "suspended") void ctx.resume();
    return ctx;
  } catch {
    return null;
  }
}

/**
 * Attack and decay as exponential ramps.
 *
 * Never to zero: `exponentialRampToValueAtTime` is undefined at 0 and silently
 * abandons the ramp, so the floor is a value low enough to be silence.
 */
function envelope(gain: AudioParam, peak: number, attack: number, decay: number, at: number) {
  gain.cancelScheduledValues(at);
  gain.setValueAtTime(0.0001, at);
  gain.exponentialRampToValueAtTime(peak, at + attack);
  gain.exponentialRampToValueAtTime(0.0001, at + attack + decay);
}

function tone(
  c: AudioContext,
  freq: number,
  peak: number,
  decay: number,
  at: number,
  type: OscillatorType = "sine",
  attack = 0.003,
) {
  const osc = c.createOscillator();
  const gain = c.createGain();
  osc.type = type;
  osc.frequency.setValueAtTime(freq, at);
  envelope(gain.gain, peak, attack, decay, at);
  osc.connect(gain).connect(c.destination);
  osc.start(at);
  osc.stop(at + attack + decay + 0.02);
}

/** Filtered noise — the body of a knuckle on wood, under the tones. */
function thud(c: AudioContext, peak: number, decay: number, at: number) {
  const frames = Math.max(1, Math.floor(c.sampleRate * decay));
  const buffer = c.createBuffer(1, frames, c.sampleRate);
  const data = buffer.getChannelData(0);
  for (let i = 0; i < frames; i += 1) data[i] = (Math.random() * 2 - 1) * (1 - i / frames);
  const source = c.createBufferSource();
  const filter = c.createBiquadFilter();
  const gain = c.createGain();
  source.buffer = buffer;
  filter.type = "lowpass";
  filter.frequency.value = 260;
  envelope(gain.gain, peak, 0.003, decay, at);
  source.connect(filter).connect(gain).connect(c.destination);
  source.start(at);
}

/** A tick as a message leaves the composer. The smallest one. */
export function playSend(): void {
  const c = audio();
  if (!c) return;
  const t = c.currentTime;
  tone(c, 1640, 0.045, 0.022, t, "sine", 0.001);
  tone(c, 980, 0.03, 0.04, t + 0.018, "triangle", 0.002);
}

/** A stamp hitting paper — a verdict landing. */
export function playStamp(): void {
  const c = audio();
  if (!c) return;
  const t = c.currentTime;
  tone(c, 92, 0.36, 0.16, t, "sine", 0.004);
  tone(c, 54, 0.22, 0.2, t, "triangle", 0.002);
  thud(c, 0.16, 0.09, t);
}

/** A clink on the till — a plan bought. */
export function playPurchase(): void {
  const c = audio();
  if (!c) return;
  const t = c.currentTime;
  tone(c, 1860, 0.07, 0.05, t, "square", 0.001);
  tone(c, 2480, 0.055, 0.07, t + 0.045, "sine", 0.001);
  tone(c, 1320, 0.045, 0.09, t + 0.085, "triangle", 0.002);
}

/** The latch, then the door — somebody let through. */
export function playThrough(): void {
  const c = audio();
  if (!c) return;
  const t = c.currentTime;
  tone(c, 3100, 0.055, 0.028, t, "square", 0.001);
  tone(c, 880, 0.04, 0.04, t + 0.028, "sine", 0.001);
  tone(c, 76, 0.26, 0.18, t + 0.07, "sine", 0.006);
  thud(c, 0.1, 0.12, t + 0.07);
}

/** Test-only: drops the cached context so a suite does not carry one between files. */
export function resetSound(): void {
  try {
    void ctx?.close();
  } catch {
    // Nothing to close.
  }
  ctx = null;
}
